import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from 'react'
import StringToReactNode from '@/app/_components/StringToReactNode/StringToReactNode'
import { Checkbox } from '@/devlink/Checkbox'
import { InputText } from '@/devlink/InputText'
import { InputNumber } from '@/devlink/InputNumber'
import TXT from '@/messages'
import { Select } from '../Forms/Select/Select'
import AdditionalInfo = Transformed.AdditionalInfo
import AdditionalInfoResponse = DataAccessInput.AdditionalInfoResponse

type Props = {
  additionalInfo: AdditionalInfo
  incomingValue: string | boolean | number | undefined
  handleUpdateValues: (
    args: Omit<AdditionalInfoResponse, 'categoryId'>,
  ) => void
}

const AdditionalInfoField: FunctionComponent<Props> = ({
  additionalInfo,
  handleUpdateValues,
  incomingValue,
}: Props) => {
  const {
    id,
    title,
    description,
    type,
    required,
    options,
    key,
  } = additionalInfo

  const [value, setValue] = useState(incomingValue)

  // reset if there is no incoming value
  // this will happen when the additionalInfoResponses are cleared
  useEffect(() => {
    setValue(incomingValue)
  }, [incomingValue])

  // map options into a format used by devlink
  const updatedOptions = [
    { v: null, t: TXT.checkoutForm.selectOne },
    ...(options || [])
      .map((el) => ({ v: el, t: el }))]

  const numericRegex = `^[0-9]${required ? '+' : '*'}$`

  const updateArgs = {
    id,
    required,
    type,
    key,
  }

  const helpText = useMemo(() => (
    <StringToReactNode incomingString={description} />
  ), [description])

  const componentProps = {
    name: id,
    id,
    placeholder: '',
    required,
  }

  switch (type) {
  case 'Boolean':
    return (
      <>
        <Checkbox
          label={title}
          // helperText={helpText}
          checkboxRuntimeProps={{
            ...componentProps,
            'data-testid': 'boolean-additional-info',
            checked: !!value,
            onChange: (evt: ChangeEvent<HTMLInputElement>) => {
              const { checked } = evt.currentTarget
              setValue(checked)
              handleUpdateValues({
                ...updateArgs,
                value: checked,
              })
            },
          }}
        />
        <div className="helptext">{helpText}</div>
      </>
    )
  case 'Select':
    if ((!options || !options.length) && updatedOptions) {
      return null
    }
    return (
      <Select
        label={title}
        name={id}
        testId="select-additional-info"
        options={updatedOptions.map(({ v, t }) => ({ value: v ?? '', label: t ?? '' }))}
        defaultValue={value ? String(value) : ''}
        onChange={(evt) => {
          const { value: updatedValue } = evt.currentTarget
          setValue(updatedValue)
          handleUpdateValues({
            ...updateArgs,
            value: updatedValue,
          })
        }}
      />
    )
  case 'Int':
    return (
      <InputNumber
        helperText={helpText}
        visibility={!!helpText}
        label={title}
        inputRuntimeProps={{
          ...componentProps,
          'data-testid': 'numeric-additional-info',
          pattern: numericRegex,
          value: value ? String(value) : '',
          onChange: (evt: ChangeEvent<HTMLInputElement>) => {
            const { value: nextValue } = evt.currentTarget
            setValue(nextValue)
            handleUpdateValues({
              ...updateArgs,
              value: nextValue,
            })
          },
        }}
      />
    )
  case 'String':
    return (
      <InputText
        helperText={helpText}
        visibility={!!helpText}
        label={title}
        inputRuntimeProps={{
          ...componentProps,
          'data-testid': 'string-additional-info',
          value: value ? String(value) : '',
          onChange: (evt: ChangeEvent<HTMLInputElement>) => {
            const { value: nextValue } = evt.currentTarget
            setValue(nextValue)
            handleUpdateValues({
              ...updateArgs,
              value: nextValue,
            })
          },
        }}
      />
    )
  default:
    return null
  }
}

export default AdditionalInfoField
